import type { EmbeddedSurface } from "@workspace/database/schema/chat";
import { excelCopilot } from "./excel";
import { freshserviceCopilot } from "./freshservice";
import { outlookCopilot } from "./outlook";
import { procoreCopilot } from "./procore";
import type {
  CopilotDefinition,
  CopilotDisplay,
  CopilotLaunch,
  EmbeddedHostFacet,
} from "./types";
import { wordCopilot } from "./word";

/**
 * Single source of truth for copilots. The embedded-host registry, the
 * `/copilots` launch page, the settings permissions UI, and the consistency
 * test all derive from this array — adding a copilot is one new entry.
 */
export const COPILOTS: readonly CopilotDefinition[] = [
  excelCopilot,
  wordCopilot,
  outlookCopilot,
  procoreCopilot,
  freshserviceCopilot,
];

export function getCopilot(id: string): CopilotDefinition | undefined {
  return COPILOTS.find((c) => c.copilotId === id);
}

// ---------------------------------------------------------------------------
// Embedded-host metadata (derived) — consumed by apps/web/lib/embedded-hosts.ts
// ---------------------------------------------------------------------------

/** A surface's embedded-host metadata, flattened from the owning copilot. */
export type EmbeddedHostMeta = EmbeddedHostFacet & { surface: EmbeddedSurface };

/** Flat, surface-keyed embedded metadata across all copilots. */
export const embeddedHostMeta: readonly EmbeddedHostMeta[] = COPILOTS.flatMap(
  (c) =>
    c.surfaces.map((surface) => ({
      surface,
      ...(c.embedded?.[surface] ?? {}),
    }))
);

// ---------------------------------------------------------------------------
// `/copilots` launch + display registry (derived) — keyed by copilot id.
// Membership = copilots that declare BOTH launch + display (Excel, Word, Outlook).
// Preserves the previous `services/domain/copilots` shape for its consumers.
// ---------------------------------------------------------------------------

export type CopilotRegistryEntry = { name: string } & CopilotDisplay & {
    launch: CopilotLaunch;
  };

function toRegistryEntry(c: CopilotDefinition): CopilotRegistryEntry {
  if (!(c.display && c.launch)) {
    throw new Error(`Copilot "${c.copilotId}" is missing display/launch`);
  }
  return { name: c.displayName, ...c.display, launch: c.launch };
}

export const COPILOT_REGISTRY = {
  excel: toRegistryEntry(excelCopilot),
  word: toRegistryEntry(wordCopilot),
  outlook: toRegistryEntry(outlookCopilot),
} as const satisfies Record<string, CopilotRegistryEntry>;

export type CopilotKey = keyof typeof COPILOT_REGISTRY & string;

export const COPILOT_KEYS = Object.keys(COPILOT_REGISTRY) as CopilotKey[];

export function isRegisteredCopilotKey(key: string): key is CopilotKey {
  return key in COPILOT_REGISTRY;
}
